import { useState } from 'react'
import { motion } from 'framer-motion'
import { Check } from 'lucide-react'
import type { AnswerValue, PhaseInfo, WorkflowStage } from '../../types/workflow'

interface ComorbidityTemplateProps {
  stage: WorkflowStage
  initialValue?: string[]
  onSelect: (value: AnswerValue) => void
  currentPhase?: PhaseInfo | null
}

const WEIGHT_RELATED_CONDITIONS = [
  { id: 'type2_diabetes', label: 'Type 2 diabetes or prediabetes' },
  { id: 'hypertension', label: 'High blood pressure (hypertension)' },
  { id: 'dyslipidemia', label: 'High cholesterol or triglycerides (dyslipidemia)' },
  { id: 'sleep_apnea', label: 'Sleep apnea' },
  { id: 'pcos', label: 'PCOS (Polycystic Ovary Syndrome)' },
  { id: 'nafld', label: 'Non-alcoholic fatty liver disease (NAFLD)' },
  { id: 'cardiovascular', label: 'Cardiovascular disease' },
  { id: 'osteoarthritis', label: 'Osteoarthritis or joint pain related to weight' },
]

export default function ComorbidityTemplate({
  initialValue,
  onSelect,
  currentPhase,
}: ComorbidityTemplateProps) {
  const [selectedConditions, setSelectedConditions] = useState<string[]>(initialValue || [])
  const [noneSelected, setNoneSelected] = useState(false)

  const handleConditionToggle = (conditionId: string) => {
    if (noneSelected) setNoneSelected(false)
    setSelectedConditions((prev) =>
      prev.includes(conditionId) ? prev.filter((id) => id !== conditionId) : [...prev, conditionId],
    )
  }

  const handleNoneToggle = () => {
    if (!noneSelected) setSelectedConditions([])
    setNoneSelected(!noneSelected)
  }

  const handleContinue = () => {
    const hasComorbidities = selectedConditions.length > 0
    onSelect({
      weight_related_conditions: selectedConditions,
      has_comorbidities: hasComorbidities,
      preliminary_pathway: hasComorbidities ? 'standard_titration' : 'requires_clinical_review',
      none_selected: noneSelected,
    })
  }

  const canContinue = selectedConditions.length > 0 || noneSelected

  return (
    <div className="flex-1 flex flex-col gap-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-4 space-y-2"
      >
        {WEIGHT_RELATED_CONDITIONS.map((condition, index) => {
          const isSelected = selectedConditions.includes(condition.id)
          return (
            <motion.button
              key={condition.id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 + index * 0.03 }}
              onClick={() => handleConditionToggle(condition.id)}
              className={`flex w-full items-center gap-3 rounded-xl border p-4 text-left transition-all duration-300 ${
                isSelected
                  ? 'border-[var(--ww-accent)] choice-selected text-[var(--ww-text)] selected-glow'
                  : 'border-[var(--ww-glass-border)] bg-[var(--ww-surface)] text-[var(--ww-text-2)] hover:border-[var(--ww-border-hover)]'
              }`}
            >
              <div
                className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded transition-colors ${
                  isSelected ? 'bg-[var(--ww-accent)]' : 'border border-[var(--ww-text-3)]'
                }`}
              >
                {isSelected && <Check className="h-3 w-3 text-white" />}
              </div>
              <span className="text-sm">{condition.label}</span>
            </motion.button>
          )
        })}
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="mb-6">
        <button
          onClick={handleNoneToggle}
          className={`flex w-full items-center gap-3 rounded-xl border p-4 text-left transition-all duration-200 ${
            noneSelected
              ? 'border-white/40 bg-white/10 text-white'
              : 'border-white/20 bg-transparent text-white/60 hover:border-white/40'
          }`}
        >
          <div
            className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full transition-colors ${
              noneSelected ? 'border-2 border-white bg-white/20' : 'border border-white/30'
            }`}
          >
            {noneSelected && <div className="h-2 w-2 rounded-full bg-white" />}
          </div>
          <span className="text-sm">None of these</span>
        </button>
      </motion.div>

      <div className="mt-auto pt-6 pb-2 -ml-[42px] w-[calc(100%+42px)]">
        <button
          onClick={handleContinue}
          disabled={!canContinue}
          className="w-full py-4 px-6 text-[15px] font-semibold text-[var(--ww-btn-text)] bg-[var(--ww-btn-bg)] border-none rounded-full cursor-pointer transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.1)] hover:not-disabled:bg-[var(--ww-btn-hover)] hover:not-disabled:-translate-y-0.5 disabled:opacity-25 disabled:cursor-default"
        >
          Continue
        </button>
      </div>
    </div>
  )
}
